import { EMPTY_ARRAY } from 'constants/defaults';
import type { ReportQuery } from 'redux/modules/superreports.types';
import type { Hour } from 'types/hour';
import { notNull, orderBy } from './typesafe';

export type AcceptableValues = string | number | null;

export interface FilterListItem {
  name: string;
  value: AcceptableValues;
  sequence?: number;
  searchValue?: string;
  groupFilter?: string;
  disabled?: boolean;
  required?: boolean;
  parent_id?: number;
  children?: Array<FilterListItem> | null;
  image?: string | null;
  imageIsAvatar?: boolean;
  active?: boolean;
}

export interface CommonOption {
  value: AcceptableValues;
  children?: Array<CommonOption> | null;
  groupFilter?: string;
  searchValue?: string | null;
  name?: string;
  sequence?: number | null;
  disabled?: boolean;
  parent_id?: AcceptableValues;
}
export interface FilterListParams<TOption extends CommonOption> {
  options: Array<TOption>;
  values: Array<AcceptableValues>;
  singleSelect?: boolean;
  singleChildSelect?: boolean;
}

export function sortOptions<TOption extends CommonOption>(options: Array<TOption>): Array<TOption> {
  return orderBy(
    options,
    [
      option => option.sequence ?? 0,
      option => (option.value === -1 ? 0 : 1),
      option => option.name ?? '',
      option => option.value,
    ],
    ['asc', 'asc', 'asc', 'asc']
  );
}

// siftOptions takes care of filtering the list based on an items children,
// what freetext searchFilter is set, and if we're limiting selection by groupFilter
interface SiftOptionsSettings {
  filterString: string;
  groupFilter?: string | null;
  flattenArray?: boolean;
  includeAllChildrenIfNoneMatch?: boolean;
}
export function siftOptions<TOption extends CommonOption>(
  options: Array<TOption> | null = [],
  settings: SiftOptionsSettings = {
    filterString: '',
    flattenArray: false,
    groupFilter: null,
    includeAllChildrenIfNoneMatch: false,
  }
): Array<TOption> {
  const filterString = settings.filterString.toLowerCase();
  let array: Array<TOption> = (options ?? [])
    .map(option => {
      // Determine if an option has children, and if they pass group & search filters
      const children =
        option.children && option.children.length
          ? option.children.filter(child => {
              // Child's group criteria:
              const groupCriteria =
                settings.groupFilter && child.groupFilter
                  ? child.groupFilter === settings.groupFilter
                  : true;
              // Return if group & search filter match
              return (
                groupCriteria &&
                (filterString === '' ||
                  // Search in option.name, or option.searchValue if provided.
                  (child.searchValue || child.name || '').toLowerCase().includes(filterString))
              );
            })
          : [];

      // Return filtered children, so we can hide empty virtualGroups below:
      return { ...option, children };
    })
    .filter(option => {
      // Parent group criteria
      const groupCriteria =
        settings.groupFilter && option.groupFilter
          ? option.groupFilter === settings.groupFilter
          : true;

      // Search in option.name, or option.searchValue if provided.
      const matchesFilter = (option.searchValue || option.name || '')
        .toLowerCase()
        .includes(filterString);

      // Return option if groupCriteria is matched and it has viable children OR search matches
      return groupCriteria && ((option.children || []).length > 0 || matchesFilter);
    })
    .map(option => {
      if (settings.includeAllChildrenIfNoneMatch && option.children && !option.children.length) {
        const originalOption = (options ?? []).find(
          opt =>
            (option.searchValue ? opt.searchValue : opt.name) ===
            (option.searchValue ? option.searchValue : option.name)
        );

        if (originalOption && originalOption.children && originalOption.children.length) {
          const children = originalOption.children;
          return { ...option, children };
        }
      }
      return option;
    });
  array = sortOptions(array);

  // Flatten array is used for keyboard navigation.
  // We filter out disabled items and those that don't match groupFilter,
  // since we don't want those accessible anymore
  if (settings.flattenArray) {
    const flatOptions: Array<TOption> = [];

    array
      .filter(opt => !opt.disabled)
      .forEach(option => {
        flatOptions.push(option);
        if (option.children && option.children.length) {
          sortOptions(option.children as Array<TOption>)
            .filter(opt => {
              const groupCriteria =
                settings.groupFilter && opt.groupFilter
                  ? opt.groupFilter === settings.groupFilter
                  : true;
              const searchValue = option.searchValue ? opt.searchValue : opt.name;
              return (
                (groupCriteria && !opt.disabled && settings.filterString === '') ||
                (groupCriteria &&
                  searchValue &&
                  searchValue.toLowerCase().includes(settings.filterString))
              );
            })
            .forEach(child => {
              flatOptions.push(child);
            });
        }
      });
    return flatOptions;
  }
  return array;
}

function unselectParentAndChildren<TOption extends CommonOption>(
  itemProps: TOption,
  params: FilterListParams<TOption>
): Array<AcceptableValues> {
  const { values } = params;
  const childrenValues = (itemProps.children || EMPTY_ARRAY).map(child => child.value);
  return values.filter(value => {
    return value !== itemProps.value && !childrenValues.includes(value);
  });
}

function selectParentAndChildren<TOption extends CommonOption>(
  itemProps: TOption,
  params: FilterListParams<TOption>
): Array<AcceptableValues> {
  const { values, singleChildSelect } = params;
  let newValues = values.filter(value => value !== itemProps.value);
  const childrenValues = (itemProps.children || []).map(child => child.value);
  const valuesExludingChildren = values.filter(value => !childrenValues.includes(value));
  if (singleChildSelect) {
    newValues = [...valuesExludingChildren, childrenValues[0], itemProps.value];
  } else {
    newValues = [...valuesExludingChildren, ...childrenValues, itemProps.value];
  }
  return newValues;
}

function deselectChildAndPossiblyParent<TOption extends CommonOption>(
  itemProps: TOption,
  params: FilterListParams<TOption>
): Array<AcceptableValues> {
  const { values, options } = params;
  const parentOption = options.find(o => o.value === itemProps.parent_id);
  const siblingItems = parentOption != null ? parentOption.children || [] : [];
  const siblingValues: Array<AcceptableValues> = siblingItems.map(child => child.value);
  const resultSelectedSiblings = siblingValues.filter(
    val => val !== itemProps.value && values.includes(val)
  ).length;
  // If we're unselecting the last child, unselect parent too:
  if (resultSelectedSiblings === 0) {
    return values.filter(val => val !== itemProps.parent_id && val !== itemProps.value);
  }
  return values.filter(val => val !== itemProps.value);
}

function selectSingleChildAndParent<TOption extends CommonOption>(
  itemProps: TOption,
  params: FilterListParams<TOption>
): Array<AcceptableValues> {
  const { values, options } = params;
  const parentOption = options.find(o => o.value === itemProps.parent_id);
  const siblingItems = parentOption != null ? parentOption.children || [] : [];
  const siblingValues: Array<AcceptableValues> = siblingItems.map(child => child.value);
  const valuesExludingChildren = values.filter(value => !siblingValues.includes(value));

  // Only select child
  if (itemProps.parent_id && values.includes(itemProps.parent_id)) {
    return [...valuesExludingChildren, itemProps.value];
  }
  // Select child & parent
  return [...valuesExludingChildren, itemProps.value, ...[itemProps.parent_id].filter(notNull)];
}

export function makeOptionSelection<TOption extends CommonOption>(
  itemProps: TOption | null | undefined,
  params: FilterListParams<TOption> = {
    options: [],
    values: [],
  }
): Array<TOption['value']> {
  if (!itemProps) {
    return [];
  }

  const { values, singleSelect, singleChildSelect } = params;
  let newValues;
  if (values.includes(itemProps.value) && !itemProps.parent_id) {
    newValues = unselectParentAndChildren(itemProps, params);
    // Select main level tag & all/1 child tags:
  } else if (!values.includes(itemProps.value) && itemProps.children && itemProps.children.length) {
    newValues = selectParentAndChildren(itemProps, params);
    // Remove child selected value (and parent, if unselecting the last child)
  } else if (values.includes(itemProps.value) && itemProps.parent_id) {
    newValues = deselectChildAndPossiblyParent(itemProps, params);
    // singleChildSelect: Select only one child (and parent, if not already selected)
  } else if (singleChildSelect && itemProps.parent_id) {
    newValues = selectSingleChildAndParent(itemProps, params);
    // Set a single value
  } else if (singleSelect) {
    newValues = [itemProps.value];
    // Select a child when parent is inactive should select parent as well:
  } else if (itemProps.parent_id && !values.includes(itemProps.parent_id)) {
    newValues = [...values, itemProps.value, itemProps.parent_id];
    // Add selected value
  } else {
    newValues = [...values, itemProps.value];
  }

  return newValues;
}

export function filterHoursByState<T>(
  hours: Array<Hour<T>>,
  filter: Partial<ReportQuery['filters']>
): Array<Hour<T>> {
  if (Array.isArray(filter.state_ids) && filter.state_ids.length > 0) {
    return hours.filter(h => filter.state_ids?.includes(h.state?.id ?? 0));
  }
  if (filter.billed == null && filter.locked == null) {
    return hours;
  }
  if (!filter.billed && filter.locked == null) {
    return hours.filter(h => !h.billed);
  }
  if (filter.billed && !filter.locked) {
    return hours.filter(h => h.billed);
  }
  if (filter.locked && !filter.billed) {
    return hours.filter(h => h.locked && h.locked_reason === 'locked');
  }

  if (filter.billed && filter.locked) {
    return hours.filter(h => h.locked);
  }

  // unbilled & unlocked only
  return hours.filter(h => !h.locked);
}
